import type {
  PreAssessmentPayload,
  PreAssessmentResponse,
  SnapshotPayload,
  SnapshotResponse,
  AudioBaselinePayload,
} from "./types/proctor.types";
import axios, { AxiosError } from "axios";

const BASE_URL = process.env.REACT_APP_PROCTORING_BASE_URL

function buildPreAssessmentFormData(
  payload: PreAssessmentPayload,
  imageFieldStyle: "flat" | "nested" = "flat"
) {
  const form = new FormData();
  form.append("user_id", payload.user_id);
  form.append("user_exam_id", payload.user_exam_id);
  form.append("assessment_id", payload.assessment_id);
  form.append("candidate_name", payload.candidate_name);
  form.append("registered_at", payload.registered_at || new Date().toISOString());

  // console.log('FormData fields:', {
  //   user_id: payload.user_id,
  //   user_exam_id: payload.user_exam_id,
  //   assessment_id: payload.assessment_id,
  //   registered_at: payload.registered_at || new Date().toISOString(),
  //   imageFieldStyle
  // });

  // Convert blobs to ensure they have proper MIME type
  const ensureBlobType = (blob: Blob, name: string) => {
    if (blob.type === 'image/jpeg') return blob;
    //console.warn(`${name} blob has type ${blob.type}, converting to image/jpeg`);
    return new Blob([blob], { type: 'image/jpeg' });
  };

  const frontBlob = ensureBlobType(payload.front, 'front');
  const rightBlob = ensureBlobType(payload.right, 'right');
  const leftBlob = ensureBlobType(payload.left, 'left');
  const ninBlob = ensureBlobType(payload.nin, 'nin');

  if (imageFieldStyle === "flat") {
    form.append("front", frontBlob, "front.jpg");
    form.append("right", rightBlob, "right.jpg");
    form.append("left", leftBlob, "left.jpg");
    form.append("nin", ninBlob, "nin.jpg");
  } else {
    // Some backend parsers expect bracket-notation for grouped files.
    form.append("images[front]", frontBlob, "front.jpg");
    form.append("images[right]", rightBlob, "right.jpg");
    form.append("images[left]", leftBlob, "left.jpg");
    form.append("images[nin]", ninBlob, "nin.jpg");
  }

  // Log all FormData entries for debugging
  // console.log('FormData entries:');
  for (const [key, value] of form.entries()) {
    if (value instanceof Blob) {
      console.log(`  ${key}: Blob(${value.size} bytes, type: ${value.type})`);
    } else {
      console.log(`  ${key}: ${value}`);
    }
  }

  return form;
}

function formatAxiosError(error: unknown, context: string) {
  if (!axios.isAxiosError(error)) {
    return `${context}: Unexpected network error`;
  }

  const axiosError = error as AxiosError<any>;
  const status = axiosError.response?.status;
  const data = axiosError.response?.data;

  if (data && typeof data === "object") {
    if (data.detail && data.error) {
      return `${data.error}: ${data.detail}`;
    }
    if (data.detail) return data.detail;
    if (data.message) return data.message;
    if (data.error) return data.error;
  }

  const body =
    typeof data === "string"
      ? data
      : data
        ? JSON.stringify(data)
        : axiosError.message;

  return `${context}${status ? ` (${status})` : ""}: ${body}`;
}

export async function uploadPreAssessment(
  payload: PreAssessmentPayload,
  onProgress?: (percent: number) => void
): Promise<PreAssessmentResponse> {
  // console.log('Pre-assessment payload:', {
  //   user_id: payload.user_id,
  //   user_exam_id: payload.user_exam_id,
  //   assessment_id: payload.assessment_id,
  //   registered_at: payload.registered_at,
  //   front: payload.front ? `Blob(${payload.front.size} bytes)` : 'missing',
  //   right: payload.right ? `Blob(${payload.right.size} bytes)` : 'missing',
  //   left: payload.left ? `Blob(${payload.left.size} bytes)` : 'missing',
  //   nin: payload.nin ? `Blob(${payload.nin.size} bytes)` : 'missing',
  // });

  const request = async (style: "flat" | "nested") => {
    const form = buildPreAssessmentFormData(payload, style);
    // console.log(`Uploading pre-assessment with ${style} form`);
    // console.log(`URL: ${BASE_URL}/pre-assessment`);

    // Explicitly delete Content-Type to let browser set it with boundary
    const config: any = {
      headers: {},
      onUploadProgress: (progressEvent: any) => {
        if (!onProgress || !progressEvent.total) return;
        const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
        onProgress(percent);
      },
      transformRequest: [(data: any) => data], // Don't transform, send FormData as-is
    };

    // Explicitly remove Content-Type so axios sets multipart with boundary
    delete config.headers['Content-Type'];

    //console.log('Request config:', config);
    //console.log('Sending FormData with keys:', Array.from(form.keys()));

    const response = await axios.post<PreAssessmentResponse>(
      `${BASE_URL}/pre-assessment`,
      form,
      config
    );

    //console.log('Upload response:', response.data);
    return response.data;
  };

  try {
    return await request("flat");
  } catch (error) {
    const message = formatAxiosError(error, "Pre-assessment upload failed");
    //console.error('First upload attempt (flat) failed:', message);

    const shouldRetryWithNested =
      message.includes("Missing images") || message.includes("required fields") || message.includes("images");

    if (!shouldRetryWithNested) {
      console.error('Not retrying with nested format');
      throw new Error(message);
    }

    console.log('Retrying with nested format...');
    try {
      return await request("nested");
    } catch (nestedError) {
      const nestedMessage = formatAxiosError(nestedError, "Pre-assessment upload failed");
      console.error('Second upload attempt (nested) failed:', nestedMessage);
      throw new Error(nestedMessage);
    }
  }
}

export async function sendSnapshot(
  payload: SnapshotPayload
): Promise<SnapshotResponse> {
  const form = new FormData();
  form.append("user_id", payload.user_id);
  form.append("user_exam_id", payload.user_exam_id);
  form.append("assessment_id", payload.assessment_id);
  form.append("image", payload.image, "snapshot.jpg");
  if (payload.violation_type) {
    form.append("violation_type", payload.violation_type);
  }

  try {
    const response = await axios.post<SnapshotResponse>(
      `${BASE_URL}/snapshot-verify`,
      form
    );
    return response.data;
  } catch (error) {
    throw new Error(formatAxiosError(error, "Snapshot verify failed"));
  }
}

export async function uploadAudioBaseline(
  payload: AudioBaselinePayload,
  onProgress?: (percent: number) => void
): Promise<any> {
  const form = new FormData();
  form.append("user_id", payload.user_id);
  form.append("user_exam_id", payload.user_exam_id);
  form.append("assessment_id", payload.assessment_id);
  form.append("audio_baseline", payload.audio_baseline, "baseline.wav");

  const config: any = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: (progressEvent: any) => {
      if (!onProgress || !progressEvent.total) return;
      const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
      onProgress(percent);
    },
  };

  try {
    const response = await axios.post(
      "https://8vlzvdalb2.execute-api.eu-west-1.amazonaws.com/prod/audio-wav-baseline",
      form,
      config
    );
    return response.data;
  } catch (error) {
    throw error;
  }
}
